/*
 * terms-gate-standard.js
 * ──────────────────────
 * Panel-managed felon / Terms & Conditions gate for STANDARD (non-AGB) product
 * pages — our replacement for the Bold (Shop Circle) "Product Options" checkbox
 * on the standalone body-armor PDPs (product.armor template, etc.).
 *
 * It fetches the panel config (GET /api/product-options-config — PUBLIC, region
 * inferred from Origin) and, if the CURRENT product is in the list and the gate
 * is enabled, injects the required checkbox into the product form and BLOCKS the
 * standard Prestige Add-to-Cart until the shopper ticks it. The attestation is a
 * byte-identical line-item property, so orders record it exactly as Bold did.
 *
 * ISOLATION (per Dorian's principle): its own file; reads state via the DOM +
 * the public config; never edits the theme's product-form JS. The AGB gear
 * builder + the helmet have their OWN server-rendered gate (terms-agreement-
 * gate.js / the snippet) and do NOT load this script — so no double gate.
 *
 * ⚠ The checkbox `name` + value are LOCKED to the order line-item property the
 * business already uses (and the AGB gate queries byte-for-byte):
 *   properties[I Agree to the Terms & Conditions & I am not a convicted felon]
 *   value ''  (unticked)  ↔  '✓'  (ticked)
 * The panel's editable "label" is only DISPLAY text — it never changes the name.
 */
(function () {
  'use strict';
  if (window.__termsGateStandardInstalled) return;
  window.__termsGateStandardInstalled = true;

  var PANEL_BASE = 'https://shopify-control-pannel-backend.onrender.com';
  var CONFIG_URL = PANEL_BASE + '/api/product-options-config';
  var CACHE_KEY = 'agb_terms_options_cfg';
  var CACHE_TTL_MS = 180000; // 3 min — a fresh add/remove propagates within this
  var CHECKED_VALUE = '✓'; // ✓ — exact value the business + AGB gate expect
  var PROP_NAME =
    'properties[I Agree to the Terms & Conditions & I am not a convicted felon]';

  // The PDP path is /products/<handle> (optionally under /collections/<x>).
  function currentHandle() {
    var m = (location.pathname || '').match(/\/products\/([^\/?#]+)/);
    return m ? decodeURIComponent(m[1]).toLowerCase() : '';
  }

  // The REAL add-to-cart form — id starts "product-form-…". NEVER the Shop Pay
  // installment form ("product-installment-form-…"), which shares action=/cart/add.
  // ⚠ Use getAttribute('id'), NOT form.id — a <form> with a child
  // <input name="id"> (Prestige's variant-id input) has form.id SHADOWED to
  // return that element, not the id string.
  function productForm() {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      if ((forms[i].getAttribute('id') || '').indexOf('product-form-') === 0) return forms[i];
    }
    for (var j = 0; j < forms.length; j++) {
      if ((forms[j].getAttribute('id') || '').indexOf('installment') === -1) return forms[j];
    }
    return null;
  }

  // Public config with a short sessionStorage cache so navigating between PDPs
  // doesn't refetch on every view. Compliance-safe: staleness only briefly
  // over- or under-shows a gate that the backend already changed.
  function loadConfig() {
    try {
      var cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        var obj = JSON.parse(cached);
        if (obj && obj.t && Date.now() - obj.t < CACHE_TTL_MS) {
          return Promise.resolve(obj.c);
        }
      }
    } catch (e) { /* ignore */ }
    return fetch(CONFIG_URL, { credentials: 'omit' })
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .then(function (c) {
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), c: c })); } catch (e) {}
        return c;
      });
  }

  function init() {
    var handle = currentHandle();
    if (!handle) return;
    // A server-rendered gate already on the page (helmet/AGB) → not our job.
    if (document.querySelector('[data-terms-checkbox]')) return;
    var form = productForm();
    if (!form) return;

    loadConfig().then(function (cfg) {
      if (!cfg || cfg.enabled === false || !Array.isArray(cfg.products)) return;
      if (document.querySelector('[data-terms-checkbox]')) return; // race guard
      var entry = null;
      for (var i = 0; i < cfg.products.length; i++) {
        var p = cfg.products[i];
        if (p && String(p.handle || '').toLowerCase() === handle) { entry = p; break; }
      }
      if (!entry) return; // not a gated product
      var variant = entry.message_variant === 'helmet' ? 'helmet' : 'plate';
      var msgs = cfg.messages || {};
      renderGate(form, {
        note: msgs[variant] || '',
        label: cfg.label || 'I Agree to the Terms & Conditions & I am not a convicted felon',
        requiredText: cfg.required_text || 'This option is required.'
      });
    }).catch(function () {});
  }

  var STYLE_ID = 'terms-gate-standard-style';
  function injectStyleOnce() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    // Mirrors snippets/terms-agreement-gate.liquid so the standard-PDP gate looks
    // identical to the helmet/AGB one.
    s.textContent =
      '.terms-agreement{margin:12px 0;padding:12px 14px;border:1px solid rgba(28,28,28,.18);border-radius:2px;background:rgba(28,28,28,.02)}' +
      '.terms-agreement__row{display:flex;align-items:flex-start;gap:10px;cursor:pointer;font-weight:600;line-height:1.35}' +
      '.terms-agreement__checkbox{flex:0 0 auto;width:18px;height:18px;margin-top:1px;accent-color:#8f1814;cursor:pointer}' +
      '.terms-agreement__star{color:#8f1814;margin-left:2px}' +
      '.terms-agreement__note{margin:8px 0 0 28px;font-size:.85em;line-height:1.4;color:#5a5a5a}' +
      '.terms-agreement__required{margin:6px 0 0 28px;font-size:.85em;font-weight:700;color:#8f1814}' +
      '.terms-agreement--invalid{border-color:#8f1814;background:rgba(143,24,20,.06)}' +
      '.terms-agreement--invalid .terms-agreement__label,.terms-agreement--invalid .terms-agreement__note{color:#8f1814}' +
      '.terms-agreement--invalid .terms-agreement__checkbox{outline:2px solid #8f1814;outline-offset:1px}';
    document.head.appendChild(s);
  }

  function renderGate(form, opts) {
    injectStyleOnce();

    var wrap = document.createElement('div');
    wrap.className = 'terms-agreement';
    wrap.setAttribute('data-terms-agreement', '');
    wrap.setAttribute('data-terms-standard', '');

    var row = document.createElement('label');
    row.className = 'terms-agreement__row';

    var checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'terms-agreement__checkbox';
    checkbox.name = PROP_NAME;   // LOCKED — order property name
    checkbox.value = '';
    checkbox.setAttribute('aria-required', 'true');
    checkbox.setAttribute('data-terms-checkbox', '');

    var labelSpan = document.createElement('span');
    labelSpan.className = 'terms-agreement__label';
    labelSpan.textContent = opts.label;
    var star = document.createElement('span');
    star.className = 'terms-agreement__star';
    star.setAttribute('aria-hidden', 'true');
    star.textContent = '*';
    labelSpan.appendChild(star);

    row.appendChild(checkbox);
    row.appendChild(labelSpan);
    wrap.appendChild(row);

    if (opts.note) {
      var note = document.createElement('p');
      note.className = 'terms-agreement__note';
      note.textContent = opts.note;
      wrap.appendChild(note);
    }
    var req = document.createElement('p');
    req.className = 'terms-agreement__required';
    req.setAttribute('data-terms-required', '');
    req.hidden = true;
    req.textContent = opts.requiredText;
    wrap.appendChild(req);

    // Must live INSIDE the product form so the checkbox becomes a line-item
    // property. Append (after variant/quantity, near the Add-to-Cart).
    form.appendChild(wrap);

    function syncValue() {
      checkbox.value = checkbox.checked ? CHECKED_VALUE : '';
      if (checkbox.checked) clearInvalid();
    }
    function markInvalid() { wrap.classList.add('terms-agreement--invalid'); req.hidden = false; }
    function clearInvalid() { wrap.classList.remove('terms-agreement--invalid'); req.hidden = true; }

    // Keep value in lock-step with checked, including browser back/forward /
    // bfcache restoration that flips `checked` WITHOUT firing 'change'.
    checkbox.addEventListener('change', syncValue);
    checkbox.addEventListener('input', syncValue);
    window.addEventListener('pageshow', syncValue);
    syncValue();
    requestAnimationFrame(syncValue);
    setTimeout(syncValue, 300);

    // Block the add when unticked. Capture phase so we run BEFORE Prestige's own
    // handler. We block BOTH the submit-button click (default = submit the form)
    // AND the form submit event — covering the theme adding via either path.
    function blockIfUnchecked(e) {
      syncValue();
      if (checkbox.value === CHECKED_VALUE) return false;
      if (e) {
        e.preventDefault();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
        else e.stopPropagation();
      }
      markInvalid();
      try { wrap.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_) {}
      return true;
    }

    form.addEventListener('submit', blockIfUnchecked, true);

    var formId = form.getAttribute('id'); // NOT form.id — shadowed by <input name="id">
    var seen = [];
    function addBtn(b) {
      if (!b || seen.indexOf(b) !== -1) return;
      seen.push(b);
      b.addEventListener('click', blockIfUnchecked, true);
    }
    // Submit buttons associated with the form by id (Prestige buy-buttons sit
    // outside the <form> and reference it via form="…"), plus any inside it.
    if (formId) {
      Array.prototype.forEach.call(
        document.querySelectorAll('[type="submit"][form="' + formId + '"], [name="add"][form="' + formId + '"]'),
        addBtn
      );
    }
    Array.prototype.forEach.call(form.querySelectorAll('[type="submit"], [name="add"]'), addBtn);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
